import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = "/api";

const PlayerContext = createContext(null);

export const PlayerProvider = ({ children }) => {
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [artist, setArtist] = useState(null);
  const [artists, setArtists] = useState({});
  const [isLooping, setIsLooping] = useState(true);
  const [isShuffled, setIsShuffled] = useState(true);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(true);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const playerRef = useRef(null);
  const hasAutoPlayed = useRef(false);

  const fetchPlaylist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tracks/playlist?shuffle=true&limit=50`);
      setPlaylist(response.data);
    } catch (error) {
      console.error('Failed to fetch playlist:', error);
      // Fallback to regular tracks
      try {
        const fallback = await axios.get(`${API}/tracks`);
        setPlaylist(fallback.data);
      } catch (e) {
        console.error('Failed to fetch tracks:', e);
      }
    }
  }, []);

  const fetchArtists = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/artists`);
      const artistMap = {};
      response.data.forEach(a => {
        artistMap[a.id] = a;
      });
      setArtists(artistMap);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  }, []);

  const playTrackAtIndex = useCallback((index) => {
    setPlaylist(currentPlaylist => {
      if (index >= 0 && index < currentPlaylist.length) {
        const track = currentPlaylist[index];
        setCurrentIndex(index);
        setCurrentTrack(track);
        setArtist(prevArtists => {
          const trackArtist = artists[track.artist_id] || { name: track.artist_name || 'Unknown Artist' };
          return trackArtist;
        });
        setIsPlaying(true);
        
        // Increment play count
        axios.post(`${API}/tracks/${track.id}/play`).catch(() => {});
      }
      return currentPlaylist;
    });
  }, [artists]);

  // Fetch playlist on mount
  useEffect(() => {
    fetchPlaylist();
    fetchArtists();
  }, [fetchPlaylist, fetchArtists]);

  // Auto-play on first load - set up the track but don't play until user interacts
  useEffect(() => {
    if (autoPlayEnabled && playlist.length > 0 && !hasAutoPlayed.current && !currentTrack) {
      hasAutoPlayed.current = true;
      // Small delay to ensure everything is loaded
      setTimeout(() => {
        // Set the track but start playing (browsers may block, but we try)
        const track = playlist[0];
        setCurrentIndex(0);
        setCurrentTrack(track);
        setArtist(artists[track.artist_id] || { name: track.artist_name || 'Unknown Artist' });
        setIsPlaying(true); // Attempt autoplay
        
        // Increment play count
        axios.post(`${API}/tracks/${track.id}/play`).catch(() => {});
      }, 500);
    }
  }, [playlist, autoPlayEnabled, currentTrack, artists]);

  // Listen for user interaction to enable autoplay
  useEffect(() => {
    const handleUserInteraction = () => {
      if (!userInteracted) {
        setUserInteracted(true);
        // If we have a track loaded but not playing, start it
        if (currentTrack && !isPlaying) {
          setIsPlaying(true);
        }
      }
    };

    // Listen for any user interaction
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('touchstart', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, [userInteracted, currentTrack, isPlaying]);

  // Track play to user history (requires token)
  const trackPlayToHistory = useCallback(async (track, token) => {
    if (!token || !track?.id) return;
    try {
      const trackType = track.artist_id ? 'user_uploaded' : 'seeded';
      await axios.post(
        `${API}/history/track-play?track_id=${track.id}&track_type=${trackType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Silently fail - history tracking is non-critical
    }
  }, []);

  const playTrack = useCallback((track, trackArtist, token = null) => {
    // Find track in playlist or add it
    const index = playlist.findIndex(t => t.id === track.id);
    if (index >= 0) {
      playTrackAtIndex(index);
    } else {
      // Add track to playlist and play
      setPlaylist(prev => [track, ...prev]);
      setCurrentIndex(0);
      setCurrentTrack(track);
      setArtist(trackArtist);
      setIsPlaying(true);
      
      // Increment play count
      axios.post(`${API}/tracks/${track.id}/play`).catch(() => {});
    }
    
    // Track to user history if token provided
    if (token) {
      trackPlayToHistory(track, token);
    }
  }, [playlist, playTrackAtIndex, trackPlayToHistory]);

  const pauseTrack = () => {
    setIsPlaying(false);
  };

  const resumeTrack = () => {
    setIsPlaying(true);
  };

  const stopTrack = () => {
    setCurrentTrack(null);
    setArtist(null);
    setIsPlaying(false);
  };

  const nextTrack = useCallback(() => {
    if (playlist.length === 0) return;
    
    let nextIndex = currentIndex + 1;
    if (nextIndex >= playlist.length) {
      if (isLooping) {
        nextIndex = 0;
      } else {
        setIsPlaying(false);
        return;
      }
    }
    playTrackAtIndex(nextIndex);
  }, [currentIndex, playlist.length, isLooping, playTrackAtIndex]);

  const prevTrack = useCallback(() => {
    if (playlist.length === 0) return;
    
    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      if (isLooping) {
        prevIndex = playlist.length - 1;
      } else {
        prevIndex = 0;
      }
    }
    playTrackAtIndex(prevIndex);
  }, [currentIndex, playlist.length, isLooping, playTrackAtIndex]);

  const shufflePlaylist = useCallback(() => {
    const shuffled = [...playlist].sort(() => Math.random() - 0.5);
    setPlaylist(shuffled);
    setIsShuffled(true);
    // Reset to first track
    if (shuffled.length > 0) {
      setCurrentIndex(0);
    }
  }, [playlist]);

  const toggleLoop = () => {
    setIsLooping(prev => !prev);
  };

  const toggleShuffle = () => {
    if (!isShuffled) {
      shufflePlaylist();
    }
    setIsShuffled(prev => !prev);
  };

  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  const handleTrackEnd = useCallback(() => {
    // Auto-advance to next track
    nextTrack();
  }, [nextTrack]);

  // Add new track to playlist (for band uploads)
  const addToPlaylist = (track) => {
    setPlaylist(prev => [...prev, track]);
  };

  // Refresh playlist (useful after new uploads)
  const refreshPlaylist = () => {
    fetchPlaylist();
  };

  return (
    <PlayerContext.Provider value={{ 
      currentTrack, 
      isPlaying, 
      artist,
      playlist,
      currentIndex,
      isLooping,
      isShuffled,
      autoPlayEnabled,
      volume,
      isMuted,
      userInteracted,
      playerRef,
      // Actions
      playTrack, 
      pauseTrack, 
      resumeTrack,
      stopTrack,
      nextTrack,
      prevTrack,
      shufflePlaylist,
      toggleLoop,
      toggleShuffle,
      toggleMute,
      setVolume,
      setAutoPlayEnabled,
      setUserInteracted,
      handleTrackEnd,
      addToPlaylist,
      refreshPlaylist,
      playTrackAtIndex,
      trackPlayToHistory
    }}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};
